import { useTranslation } from "react-i18next";
import { hasAutomaticStudySource, mergeAssignedCraMembers, type Study } from "@/services/studies";

interface StudiesTableProps {
  studies: Study[];
  loading?: boolean;
  onView: (study: Study, memberSource?: "auto" | "manual") => void;
  onEdit: (study: Study) => void;
}

export default function StudiesTable({
  studies,
  loading = false,
  onView,
  onEdit,
}: StudiesTableProps) {
  const { t } = useTranslation();

  if (!loading && studies.length === 0) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl flex flex-col items-center justify-center py-20 text-center">
        <span className="w-10 h-10 flex items-center justify-center mb-3">
          <i className="ri-flask-line text-3xl text-gray-300"></i>
        </span>
        <p className="text-sm font-medium text-gray-400">{t("studies.table.noResults")}</p>
        <p className="text-xs text-gray-300 mt-1">{t("studies.table.noResultsHint")}</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
      <table className="w-full table-fixed font-sans text-sm">
        <colgroup>
          <col style={{ width: "28%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "30%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "10%" }} />
        </colgroup>
        <thead>
          <tr style={{ backgroundColor: "#fafafa" }}>
            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100">
              {t("studies.table.name")}
            </th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100">
              {t("studies.table.code")}
            </th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100">
              {t("studies.table.members")}
            </th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100">
              {t("studies.table.createdAt")}
            </th>
            <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100">
              {t("studies.table.actions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} className="px-6 py-16 text-center text-sm text-gray-400">
                <i className="ri-loader-4-line text-2xl animate-spin inline-block mb-2"></i>
                <div>{t("common.loading")}</div>
              </td>
            </tr>
          ) : (
            studies.map((study, idx) => {
              const isLast = idx === studies.length - 1;
              const merged = mergeAssignedCraMembers(study);
              const autoCount = merged.filter(hasAutomaticStudySource).length;
              const manualCount = merged.filter((m) => m.manuallyAssigned).length;
              const hasMembers = merged.length > 0;

              return (
                <tr
                  key={study.id}
                  className="hover:bg-gray-50/60 transition-colors"
                  style={{
                    borderBottom: isLast ? "none" : "1px solid #f3f4f6",
                  }}
                >
                  {/* Study Name */}
                  <td className="px-5 py-4">
                    <div className="text-sm font-medium text-gray-800 leading-snug">
                      {study.name}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="text-[11px] text-gray-400">
                        {study.reports} {t("studies.table.reports")}
                      </span>
                    </div>
                  </td>

                  {/* Study Code */}
                  <td className="px-4 py-4">
                    <span
                      className="whitespace-nowrap rounded px-2 py-1 text-xs"
                      style={{ backgroundColor: "rgba(131,0,81,0.06)", color: "#830051" }}
                    >
                      {study.code}
                    </span>
                  </td>

                  {/* Members */}
                  <td className="px-4 py-4">
                    {hasMembers ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-800">{merged.length}</span>
                        <div className="flex items-center gap-1.5">
                          {autoCount > 0 && (
                            <button
                              type="button"
                              onClick={() => onView(study, "auto")}
                              className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap cursor-pointer transition-colors hover:border-gray-300 hover:text-gray-700"
                              style={{
                                backgroundColor: "#f3f4f6",
                                color: "#6b7280",
                                border: "1px solid #e5e7eb",
                              }}
                            >
                              {autoCount} Auto Sync
                            </button>
                          )}
                          {manualCount > 0 && (
                            <button
                              type="button"
                              onClick={() => onView(study, "manual")}
                              className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap cursor-pointer transition-opacity hover:opacity-80"
                              style={{
                                backgroundColor: "rgba(131,0,81,0.08)",
                                color: "#830051",
                                border: "1px solid rgba(131,0,81,0.15)",
                              }}
                            >
                              {manualCount} Manual
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>

                  {/* Created At */}
                  <td className="px-4 py-4">
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {study.createdAt}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onEdit(study)}
                        className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                        title={t("common.edit")}
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => onView(study)}
                        className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                        title={t("common.viewDetails")}
                      >
                        <i className="ri-eye-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
